"use client";

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessagesSquare, ShieldCheck, Users } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Switch } from "@/app/_module/components/ui/switch";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { forumService } from "@/app/(authenticated)/forum/_modules/components/ForumService";
import { useForumAccess } from "@/app/(authenticated)/forum/_modules/hooks/useForumAccess";
import { FORUM_ENABLED } from "@/app/_module/utils/featureFlags";

// The Community switch (ON by default, opt-out). Agency owners flip it for their whole agency (inherited
// down the tree); independent agents with no agency flip it for their own
// account. Optimistic with revert-on-error; invalidates the eligibility query
// so the sidebar entry reacts immediately.
export function ForumSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const { eligibility } = useForumAccess();

  const settingsQuery = useQuery({
    queryKey: ["forum", "agency-settings"],
    queryFn: forumService.getAgencySettings,
    staleTime: 30_000,
    enabled: FORUM_ENABLED,
  });

  useEffect(() => {
    if (settingsQuery.data) setEnabled(settingsQuery.data.enabled);
  }, [settingsQuery.data]);

  // Lives inside Preferences → General, so it decides its OWN visibility:
  //  - pre-launch / enterprise / white-label: eligibility = NOT_AVAILABLE → hidden
  //  - agents inside an agency (non-owners): canToggleAgency=false → hidden
  //  - owners and solo accounts: shown, on or off
  const visible =
    FORUM_ENABLED &&
    !!eligibility &&
    (eligibility.canToggleAgency || (eligibility.isModerator && settingsQuery.data?.hasAgency));
  if (!visible) return null;

  const scope = settingsQuery.data?.scope || (settingsQuery.data?.hasAgency ? "agency" : "user");
  const isAgency = scope === "agency";

  const flip = async (value: boolean) => {
    setEnabled(value); // optimistic
    setSaving(true);
    try {
      await forumService.setAgencySettings(value);
      queryClient.invalidateQueries({ queryKey: ["agency-features"] });
      queryClient.invalidateQueries({ queryKey: ["forum", "eligibility"] });
      queryClient.invalidateQueries({ queryKey: ["forum", "agency-settings"] });
      toast({
        title: value ? "Community turned on" : "Community turned off",
        description: value
          ? isAgency
            ? "The Forum tab is live for everyone in your agency."
            : "The Forum tab is now in your sidebar."
          : isAgency
            ? "The Forum tab is hidden for your agency."
            : "The Forum tab is hidden.",
        duration: 4000,
      });
    } catch (e: any) {
      setEnabled(!value); // revert
      toast({ title: "Couldn't save", description: e.message, variant: "destructive", duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const canToggle = settingsQuery.data?.canToggle;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessagesSquare className="h-4 w-4 text-primary" />
          Community
        </CardTitle>
        <CardDescription>
          The Producer Floor: one forum for independent InsuraCentral agents, across agencies, under real names.
          Questions, wins, scripts, carriers, case help.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div className="pr-4">
            <p className="text-sm font-medium">{isAgency ? "Show the Forum tab for your agency" : "Show the Forum tab"}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {isAgency
                ? "Turns it on for you and every agent in your agency, including sub-agencies."
                : "Turns the Community on for your account. You can turn it off any time."}
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={flip} disabled={!canToggle || saving} aria-label="Show the Forum tab" />
        </div>

        {!canToggle && <p className="text-xs text-muted-foreground">Only the agency owner can change this setting.</p>}

        <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
          <div className="flex items-start gap-2 rounded-md bg-muted/50 p-2">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            Client identifiers are blocked before anything posts: SSNs, dates of birth, Medicare IDs, and contact details that match your own leads.
          </div>
          <div className="flex items-start gap-2 rounded-md bg-muted/50 p-2">
            <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            Reading is open to your whole team. Posting needs an active subscription or trial.
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
